// src/components/FileManager/FileManagerPanel.jsx
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Lock,
  Link2,
  BookOpen,
  Folder,
  FolderOpen,
} from "lucide-react";

function normStr(x) {
  return String(x ?? "").trim();
}

function asLower(x) {
  return normStr(x).toLowerCase();
}

/**
 * Optional grouping config (view.grouping)
 * ---------------------------------------------------------------------------
 * Purpose: allow “Windows folders” style collapsible groups for ANY module.
 *
 * Shape:
 *  grouping: {
 *    enabled?: boolean,            // default true if provided
 *    key: string,                 // e.g. "issuer"
 *    header?: string,             // optional, e.g. "Issuer"
 *    value?: (row) => any,        // if omitted, tries to use matching column.sortValue(row), else row[key]
 *    label?: (row) => string,     // if omitted, uses normStr(value(row)) or "—"
 *    groupSortValue?: (group) => any,
 *    groupSortAsc?: boolean,      // default true
 *    defaultCollapsed?: boolean,  // default true
 *    showToggleAll?: boolean,     // default true
 *  }
 */

export default function FileManagerPanel({
  entityName = "Record",
  adapter,
  view,
  onSelect,
  onClose, // used by modal wrapper, optional for inline
  showToolbar = true,
  showFooter = true,

  // When true, panel renders its own "card" surface (border/rounded/bg/shadow).
  // When false, panel renders "bare" so the parent can own the surface (FileRibbon, Modal, etc).
  framed = true,

  className = "",
  maxHeightClassName = "max-h-[80vh]",
}) {
  if (!adapter) {
    throw new Error("FileManagerPanel requires an `adapter` prop.");
  }
  if (!view) {
    throw new Error("FileManagerPanel requires a `view` prop.");
  }

  const {
    records = [],
    loading = false,
    error = null,

    // Support both adapter conventions:
    // - older adapters: isReadOnlyRecord
    // - current adapters: isLockedRecord
    isReadOnlyRecord,
    isLockedRecord,
  } = adapter;

  const {
    title,
    columns = [],
    defaultSort = { key: columns?.[0]?.key, asc: true },
    search: searchCfg = null,
    selectedLabel,
    grouping: groupingCfg = null,
  } = view;

  const [selected, setSelected] = useState(null);
  const [sortKey, setSortKey] = useState(defaultSort?.key || columns?.[0]?.key);
  const [sortAsc, setSortAsc] = useState(
    typeof defaultSort?.asc === "boolean" ? defaultSort.asc : true
  );
  const [search, setSearch] = useState("");

  // Collapsible groups (Set contains group ids that are currently collapsed)
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

  // Track grouping signature so we only reset collapse state when grouping settings change
  const groupingSigRef = useRef("");

  // Track the last-seen group ids so we can detect truly-new groups
  const prevGroupIdsRef = useRef(new Set());

  // Option 1: fixed layout widths + truncation
  const DEFAULT_COL_WIDTHS = useMemo(() => {
    // If the view provides explicit widths, honor them and do not force defaults.
    const hasAnyExplicitWidth = columns.some((c) =>
      /\bw-\[|\bw-\d|\bmin-w-|\bmax-w-|\bflex\b/.test(
        String(c?.headerClassName ?? "") + " " + String(c?.cellClassName ?? "")
      )
    );

    if (hasAnyExplicitWidth) return null;

    // Defaults:
    // - first 2 columns are large (text columns)
    // - remaining columns are fixed-ish so they don’t get pushed off-canvas
    return columns.map((c, idx) => {
      if (idx === 0) return { key: c.key, th: "w-[32%]", td: "" };
      if (idx === 1) return { key: c.key, th: "w-[40%]", td: "" };

      if (c.align === "right") return { key: c.key, th: "w-[14%]", td: "" };
      return { key: c.key, th: "w-[14%]", td: "" };
    });
  }, [columns]);

  const getDefaultWidthClass = useCallback(
    (colKey, kind) => {
      if (!DEFAULT_COL_WIDTHS) return "";
      const hit = DEFAULT_COL_WIDTHS.find((x) => x.key === colKey);
      if (!hit) return "";
      return kind === "th" ? hit.th : hit.td;
    },
    [DEFAULT_COL_WIDTHS]
  );

  useEffect(() => {
    if (defaultSort?.key) setSortKey(defaultSort.key);
    if (typeof defaultSort?.asc === "boolean") setSortAsc(defaultSort.asc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSort?.key, defaultSort?.asc]);

  // If the record list changes, keep selection if still present, otherwise clear.
  useEffect(() => {
    if (!selected?.id) return;
    const stillExists = (Array.isArray(records) ? records : []).some(
      (r) => r?.id === selected.id
    );
    if (!stillExists) setSelected(null);
  }, [records, selected?.id]);

  const rowIsReadOnly = (r) => {
    if (typeof isReadOnlyRecord === "function") return !!isReadOnlyRecord(r);
    if (typeof isLockedRecord === "function") return !!isLockedRecord(r);
    // last-resort fallback if adapter annotated rows
    if (typeof r?.isLocked === "boolean") return r.isLocked;
    return false;
  };

  const rowIsLinked = (r) => {
    if (!r) return false;
    return !!r.linked_ds_deal_id;
  };

  // Prefer adapter-normalized boolean if present, fall back to ds_deals.deal_class
  const rowIsLibraryDeal = (r) => {
    if (!r) return false;
    if (typeof r.isLibrary === "boolean") return r.isLibrary;
    return String(r?.deal_class ?? "").trim().toLowerCase() === "library";
  };

  const sortCol = useMemo(() => {
    return columns.find((c) => c.key === sortKey) || null;
  }, [columns, sortKey]);

  const filteredRecords = useMemo(() => {
    const list = Array.isArray(records) ? records : [];
    const q = searchCfg ? search.trim().toLowerCase() : "";

    if (!searchCfg || !q) return list;

    if (typeof searchCfg.predicate === "function") {
      return list.filter((r) => {
        try {
          return !!searchCfg.predicate(r, q);
        } catch {
          return false;
        }
      });
    }

    const textCols = columns.filter((c) => typeof c.searchText === "function");
    if (textCols.length === 0) return list;

    return list.filter((r) => {
      const hay = textCols
        .map((c) => normStr(c.searchText(r)).toLowerCase())
        .join(" | ");
      return hay.includes(q);
    });
  }, [records, search, searchCfg, columns]);

  const sortedRecords = useMemo(() => {
    const list = Array.isArray(filteredRecords) ? filteredRecords : [];
    const col = sortCol;

    if (!col || typeof col.sortValue !== "function") return list;

    const rows = [...list];
    rows.sort((a, b) => {
      const aVal = col.sortValue(a);
      const bVal = col.sortValue(b);

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortAsc ? 1 : -1;
      if (bVal == null) return sortAsc ? -1 : 1;

      if (typeof aVal === "number" && typeof bVal === "number") {
        if (aVal < bVal) return sortAsc ? -1 : 1;
        if (aVal > bVal) return sortAsc ? 1 : -1;
        return 0;
      }

      if (aVal instanceof Date && bVal instanceof Date) {
        const at = aVal.getTime();
        const bt = bVal.getTime();
        if (at < bt) return sortAsc ? -1 : 1;
        if (at > bt) return sortAsc ? 1 : -1;
        return 0;
      }

      const as = asLower(aVal);
      const bs = asLower(bVal);
      if (as < bs) return sortAsc ? -1 : 1;
      if (as > bs) return sortAsc ? 1 : -1;
      return 0;
    });

    return rows;
  }, [filteredRecords, sortCol, sortAsc]);

  const renderSortIcon = (colKey) => {
    if (sortKey !== colKey) return <ArrowUpDown className="w-3.5 h-3.5" />;
    return sortAsc ? (
      <ArrowUp className="w-3.5 h-3.5 text-app" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-app" />
    );
  };

  const handleSortClick = (col) => {
    if (!col || typeof col.sortValue !== "function") return;

    if (sortKey === col.key) {
      setSortAsc((v) => !v);
      return;
    }

    setSortKey(col.key);
    if (typeof col.defaultAsc === "boolean") setSortAsc(col.defaultAsc);
    else setSortAsc(true);
  };

  const selectedFooterLabel = useMemo(() => {
    if (!selected) return "";
    if (typeof selectedLabel === "function") {
      try {
        return selectedLabel(selected);
      } catch {
        return "";
      }
    }
    return "";
  }, [selected, selectedLabel]);

  const panelTitle = title || `Open ${entityName}`;

  const shellCls = framed
    ? "bg-[hsl(var(--background))] text-app rounded-xl shadow-sm border border-border flex flex-col min-h-0"
    : "text-app flex flex-col min-h-0 h-full";

  // Important: sticky header must paint an opaque background even when framed=false.
  // Using tokens keeps it consistent with both modal and inline surfaces.
  const headBgCls = "bg-app";

  // When the parent (modal) owns the close "X" chrome, reserve space so the
  // search input doesn’t collide with it.
  const toolbarChromePad = !framed ? "pr-12" : "";

  const isDateishColumn = useCallback((c) => {
    const k = asLower(c?.key);
    return k.includes("date");
  }, []);

  /* ---------------------------------------------------------------------------
   * Grouping
   * ------------------------------------------------------------------------ */

  const grouping = useMemo(() => {
    if (!groupingCfg) return null;
    const enabled =
      typeof groupingCfg.enabled === "boolean" ? groupingCfg.enabled : true;
    if (!enabled) return null;

    const key = normStr(groupingCfg.key);
    if (!key) return null;

    const col = columns.find((c) => c.key === key) || null;

    const valueFn =
      typeof groupingCfg.value === "function"
        ? groupingCfg.value
        : col && typeof col.sortValue === "function"
        ? (r) => col.sortValue(r)
        : (r) => r?.[key];

    const labelFn =
      typeof groupingCfg.label === "function"
        ? groupingCfg.label
        : (r) => {
            const v = valueFn(r);
            const s = normStr(v);
            return s || "—";
          };

    const header = normStr(groupingCfg.header) || "Group";
    const defaultCollapsed =
      typeof groupingCfg.defaultCollapsed === "boolean"
        ? groupingCfg.defaultCollapsed
        : true;

    const groupSortAsc =
      typeof groupingCfg.groupSortAsc === "boolean"
        ? groupingCfg.groupSortAsc
        : true;

    const groupSortValue =
      typeof groupingCfg.groupSortValue === "function"
        ? groupingCfg.groupSortValue
        : (g) => asLower(g?.label);

    const showToggleAll =
      typeof groupingCfg.showToggleAll === "boolean"
        ? groupingCfg.showToggleAll
        : true;

    return {
      key,
      header,
      valueFn,
      labelFn,
      defaultCollapsed,
      groupSortAsc,
      groupSortValue,
      showToggleAll,
    };
  }, [groupingCfg, columns]);

  const grouped = useMemo(() => {
    if (!grouping) return null;

    const list = Array.isArray(sortedRecords) ? sortedRecords : [];
    const map = new Map();

    for (const r of list) {
      const label = grouping.labelFn(r);
      const rawVal = grouping.valueFn(r);

      // Prefer stable ids. If valueFn returns a non-primitive, use the label.
      const rawIsStable =
        rawVal == null ||
        typeof rawVal === "string" ||
        typeof rawVal === "number" ||
        typeof rawVal === "boolean";
      const groupId = rawIsStable ? normStr(rawVal) : normStr(label);

      const gid = groupId || label || "—";

      if (!map.has(gid)) {
        map.set(gid, {
          id: gid,
          label: normStr(label) || "—",
          rows: [],
        });
      }
      map.get(gid).rows.push(r);
    }

    const groups = Array.from(map.values());

    const effectiveGroupSortAsc =
      sortKey === grouping.key ? sortAsc : grouping.groupSortAsc;

    const effectiveGroupSortValue =
      sortKey === grouping.key
        ? (g) => asLower(g?.label)
        : grouping.groupSortValue;

    groups.sort((a, b) => {
      const av = effectiveGroupSortValue(a);
      const bv = effectiveGroupSortValue(b);

      if (av == null && bv == null) return 0;
      if (av == null) return effectiveGroupSortAsc ? 1 : -1;
      if (bv == null) return effectiveGroupSortAsc ? -1 : 1;

      const as = asLower(av);
      const bs = asLower(bv);
      if (as < bs) return effectiveGroupSortAsc ? -1 : 1;
      if (as > bs) return effectiveGroupSortAsc ? 1 : -1;
      return 0;
    });

    return groups;
  }, [grouping, sortedRecords, sortKey, sortAsc]);

  // Preserve expanded/collapsed state across sorts/search changes.
  // Only reset when grouping configuration changes (key/defaultCollapsed).
  useLayoutEffect(() => {
    if (!grouping || !grouped) return;

    const sig = `${grouping.key}::${grouping.defaultCollapsed ? "1" : "0"}`;

    setCollapsedGroups((prev) => {
      const groupIds = new Set(grouped.map((g) => g.id));
      const isNewGrouping = groupingSigRef.current !== sig;

      if (isNewGrouping) {
        groupingSigRef.current = sig;
        prevGroupIdsRef.current = groupIds;

        if (grouping.defaultCollapsed) {
          return new Set(grouped.map((g) => g.id));
        }
        return new Set();
      }

      const next = new Set();

      for (const id of prev) {
        if (groupIds.has(id)) next.add(id);
      }

      if (grouping.defaultCollapsed) {
        const prevGroupIds = prevGroupIdsRef.current || new Set();
        for (const id of groupIds) {
          if (!prevGroupIds.has(id)) next.add(id);
        }
      }

      prevGroupIdsRef.current = groupIds;
      return next;
    });
  }, [grouping, grouped]);

  const toggleGroup = useCallback((groupId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const collapseAll = useCallback(() => {
    if (!grouped) return;
    setCollapsedGroups(new Set(grouped.map((g) => g.id)));
  }, [grouped]);

  const expandAll = useCallback(() => {
    setCollapsedGroups(new Set());
  }, []);

  const allCollapsed = useMemo(() => {
    if (!grouping || !grouped || grouped.length === 0) return false;
    return grouped.every((g) => collapsedGroups.has(g.id));
  }, [grouping, grouped, collapsedGroups]);

  return (
    <div className={[shellCls, className].filter(Boolean).join(" ")}>
      {/* Toolbar */}
      {showToolbar ? (
        <div
          className={[
            "shrink-0 flex items-center justify-between px-5 py-3 border-b border-border gap-3",
            toolbarChromePad,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <h3 className="font-bold text-sm text-app truncate">{panelTitle}</h3>

          <div className="flex items-center gap-3">
            {grouping?.showToggleAll ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn btn-ghost h-6 px-2 text-sm"
                  onClick={() => {
                    if (allCollapsed) expandAll();
                    else collapseAll();
                  }}
                  title={allCollapsed ? "Expand all groups" : "Collapse all groups"}
                  disabled={!grouped || grouped.length === 0}
                >
                  {allCollapsed ? "Expand All" : "Collapse All"}
                </button>
              </div>
            ) : null}

            {searchCfg ? (
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchCfg.placeholder || "Search…"}
                className="h-8 w-56 max-w-full rounded-md border border-border bg-app px-2 text-sm text-app placeholder:text-muted"
                aria-label={`Search ${entityName.toLowerCase()}s`}
              />
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Body */}
      <div
        className={[
          "flex-1 min-h-0 overflow-y-auto",
          !framed ? "" : maxHeightClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {loading ? (
          <div className="p-5 text-muted text-sm">
            Loading {entityName.toLowerCase()}s…
          </div>
        ) : error ? (
          <div className="p-5 text-red-500">Error: {error}</div>
        ) : sortedRecords.length === 0 ? (
          <div className="p-5 text-muted text-sm">
            {searchCfg && search.trim()
              ? `No matches for “${search.trim()}”.`
              : `No saved ${entityName.toLowerCase()}s found.`}
          </div>
        ) : (
          <table className="w-full text-sm table-fixed">
            <thead
              className={[
                "sticky top-0 z-20",
                headBgCls,
                "shadow-[inset_0_-1px_0_hsl(var(--border))]",
              ].join(" ")}
            >
              <tr className="text-muted select-none">
                {columns.map((c) => {
                  const canSort = typeof c.sortValue === "function";
                  const align =
                    c.align === "right"
                      ? "text-right"
                      : c.align === "center"
                      ? "text-center"
                      : "text-left";

                  return (
                    <th
                      key={c.key}
                      className={[
                        "px-4 py-2 font-bold",
                        align,
                        canSort ? "cursor-pointer" : "",
                        c.headerClassName || getDefaultWidthClass(c.key, "th"),
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => handleSortClick(c)}
                      title={canSort ? `Sort by ${c.header}` : undefined}
                    >
                      <div
                        className={[
                          "flex items-center gap-1 min-w-0",
                          c.align === "right"
                            ? "justify-end"
                            : c.align === "center"
                            ? "justify-center"
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <span className="truncate">{c.header}</span>
                        {canSort ? renderSortIcon(c.key) : null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {/* Grouped rendering */}
              {grouping && grouped
                ? grouped.map((g) => {
                    const collapsed = collapsedGroups.has(g.id);
                    const count = Array.isArray(g.rows) ? g.rows.length : 0;

                    return (
                      <React.Fragment key={`group-${g.id}`}>
                        {/* Group row */}
                        <tr
                          className="select-none"
                          onClick={() => toggleGroup(g.id)}
                          title="Click to expand/collapse"
                        >
                          <td
                            colSpan={columns.length}
                            className="px-4 py-2 bg-white/5 hover:bg-white/10 cursor-pointer"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {collapsed ? (
                                <Folder className="w-4 h-4 text-muted shrink-0" />
                              ) : (
                                <FolderOpen className="w-4 h-4 text-muted shrink-0" />
                              )}

                              <div className="min-w-0 flex-1 truncate font-semibold text-app">
                                {g.label || "—"}
                              </div>

                              <div className="text-xs text-muted whitespace-nowrap shrink-0">
                                {count}{" "}
                                {count === 1
                                  ? entityName.toLowerCase()
                                  : `${entityName.toLowerCase()}s`}
                              </div>
                            </div>
                          </td>
                        </tr>

                        {/* Child rows */}
                        {!collapsed
                          ? g.rows.map((r) => {
                              const ro = rowIsReadOnly(r);
                              const linked = rowIsLinked(r);
                              const isLibrary = rowIsLibraryDeal(r);

                              return (
                                <tr
                                  key={r.id}
                                  className={`cursor-pointer hover:bg-white/5 ${
                                    selected?.id === r.id ? "bg-white/10" : ""
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelected(r);
                                  }}
                                  onDoubleClick={(e) => {
                                    e.stopPropagation();
                                    onSelect?.(r);
                                  }}
                                >
                                  {columns.map((c, idx) => {
                                    const align =
                                      c.align === "right"
                                        ? "text-right"
                                        : c.align === "center"
                                        ? "text-center"
                                        : "text-left";

                                    const showBadges =
                                      !!c.showReadOnlyBadge ||
                                      !!c.showLinkedBadge ||
                                      !!c.showLibraryBadge;

                                    const indentFirstCol = idx === 0;

                                    const nowrap = isDateishColumn(c)
                                      ? "whitespace-nowrap tabular-nums"
                                      : "";

                                    return (
                                      <td
                                        key={c.key}
                                        className={[
                                          "px-4 py-2",
                                          align,
                                          nowrap,
                                          c.cellClassName ||
                                            getDefaultWidthClass(c.key, "td"),
                                        ]
                                          .filter(Boolean)
                                          .join(" ")}
                                      >
                                        <div
                                          className={[
                                            "min-w-0",
                                            indentFirstCol ? "ml-6" : "",
                                          ]
                                            .filter(Boolean)
                                            .join(" ")}
                                        >
                                          {showBadges ? (
                                            <div className="flex items-center gap-2 min-w-0">
                                              <div className="min-w-0 flex-1">
                                                {typeof c.render === "function"
                                                  ? c.render(r)
                                                  : null}
                                              </div>

                                              {c.showLinkedBadge && linked ? (
                                                <span
                                                  className="flex items-center gap-1 text-xs text-muted whitespace-nowrap shrink-0"
                                                  title="Linked to issue"
                                                >
                                                  <Link2 className="w-3 h-3" />
                                                  <span>Linked</span>
                                                </span>
                                              ) : null}

                                              {c.showLibraryBadge && isLibrary ? (
                                                <span
                                                  className="inline-flex items-center justify-center text-muted shrink-0"
                                                  title="Library Issue"
                                                >
                                                  <BookOpen className="w-4 h-4" />
                                                </span>
                                              ) : null}

                                              {c.showReadOnlyBadge && ro ? (
                                                <span
                                                  className="inline-flex items-center justify-center text-muted shrink-0"
                                                  title="Read Only"
                                                >
                                                  <Lock className="w-4 h-4" />
                                                </span>
                                              ) : null}
                                            </div>
                                          ) : typeof c.render === "function" ? (
                                            c.render(r)
                                          ) : null}
                                        </div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })
                          : null}
                      </React.Fragment>
                    );
                  })
                : // Ungrouped rendering
                  sortedRecords.map((r) => {
                    const ro = rowIsReadOnly(r);
                    const linked = rowIsLinked(r);
                    const isLibrary = rowIsLibraryDeal(r);

                    return (
                      <tr
                        key={r.id}
                        className={`cursor-pointer hover:bg-white/5 ${
                          selected?.id === r.id ? "bg-white/10" : ""
                        }`}
                        onClick={() => setSelected(r)}
                        onDoubleClick={() => onSelect?.(r)}
                      >
                        {columns.map((c) => {
                          const align =
                            c.align === "right"
                              ? "text-right"
                              : c.align === "center"
                              ? "text-center"
                              : "text-left";

                          const showBadges =
                            !!c.showReadOnlyBadge ||
                            !!c.showLinkedBadge ||
                            !!c.showLibraryBadge;

                          const nowrap = isDateishColumn(c)
                            ? "whitespace-nowrap tabular-nums"
                            : "";

                          return (
                            <td
                              key={c.key}
                              className={[
                                "px-4 py-2",
                                align,
                                nowrap,
                                c.cellClassName || getDefaultWidthClass(c.key, "td"),
                              ]
                                .filter(Boolean)
                                .join(" ")}
                            >
                              {showBadges ? (
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="min-w-0 flex-1">
                                    {typeof c.render === "function"
                                      ? c.render(r)
                                      : null}
                                  </div>

                                  {c.showLinkedBadge && linked ? (
                                    <span
                                      className="flex items-center gap-1 text-xs text-muted whitespace-nowrap shrink-0"
                                      title="Linked to issue"
                                    >
                                      <Link2 className="w-3 h-3" />
                                      <span>Linked</span>
                                    </span>
                                  ) : null}

                                  {c.showLibraryBadge && isLibrary ? (
                                    <span
                                      className="flex items-center gap-1 text-xs text-muted whitespace-nowrap shrink-0"
                                      title="Library Issue"
                                    >
                                      <BookOpen className="w-3 h-3" />
                                      <span>Library</span>
                                    </span>
                                  ) : null}

                                  {c.showReadOnlyBadge && ro ? (
                                    <span className="flex items-center gap-1 text-xs text-muted whitespace-nowrap shrink-0">
                                      <Lock className="w-3 h-3" />
                                      Read Only
                                    </span>
                                  ) : null}
                                </div>
                              ) : typeof c.render === "function" ? (
                                c.render(r)
                              ) : null}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      {showFooter ? (
        <div className="shrink-0 flex justify-between items-center px-5 py-3 border-t border-border gap-4">
          <input
            type="text"
            className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={selectedFooterLabel}
            readOnly
          />

          <div className="flex gap-2">
            {onClose ? (
              <button className="btn btn-ghost" onClick={onClose}>
                Cancel
              </button>
            ) : null}
            <button
              className="btn btn-primary"
              disabled={!selected}
              onClick={() => onSelect?.(selected)}
            >
              Open
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
